// Auditioning a list of sounds through the one engine (SI-4): the bar's item
// for a sound, with its call to action, Add to session (SI-25), share and,
// on phones, the price pill; and the ↑ ↓ Space keys of an audition list.
//
//   barItem(sound, { me })                 the PlayerBar / MiniPlayer item
//   playIn(sound, list, { me })            toggle it, or start it with the
//                                          list as the queue
//   step(list, delta, { me })              the next or previous sound
//   redecorate(list, { me })               re-dress the bar's item after the
//                                          bar's own next / prev (see below)
import audition from "@/audio/audition.js";
import nowPlaying from "@/audio/nowPlaying.js";
import { displayKey } from "@/kit/musicKey.js";
import { forName } from "@/kit/people.js";
import { canBuy, canDownload, keyBpmText, soundCta } from "@/kit/sound.js";
import { prettySessionName } from "@/kit/sessionName.js";
import { addToSession, canAddToSession } from "@/services/addToSession.js";
import { download, shareLink } from "@/services/soundActions.js";
import { soundSheets } from "@/shell/soundSheets.js";

const MARK = "w3SoundItem";

// "Dorian Sketches · Priya S.": where it is from, then who made it (the
// app's auditionItemFor; MacSounds' bar and the Sounds board's mini player).
export function whereOf(sound) {
  if (!sound) return "";
  return (
    sound.sessionDisplay ||
    (sound.packs && sound.packs[0] && sound.packs[0].title) ||
    prettySessionName(sound.sessionName || "") ||
    ""
  );
}

export function barSubtitle(sound, { showCreator = true } = {}) {
  return [whereOf(sound), showCreator ? sound.creatorDisplay || sound.creator : null].filter(Boolean).join(" · ");
}

// The call to action does what it says: download, or the buy sheet.
export function runCta(sound) {
  if (canDownload(sound)) download(sound);
  else if (canBuy(sound)) soundSheets.open("buy", sound);
}

export function barItem(sound, { me = null, color = null, showCreator = true } = {}) {
  const cta = soundCta(sound);
  const item = audition.soundItem(sound, {
    color: color || forName(sound.creator, me),
    cta: { ...cta, onClick: cta.disabled ? null : () => runCta(sound) },
    addToSession: canAddToSession(sound) ? () => addToSession(sound) : null,
    share: () => shareLink(sound, me),
    pricePill: canBuy(sound) && !canDownload(sound) ? { label: sound.chip.label, onClick: () => soundSheets.open("buy", sound) } : null,
  });
  // MacSounds' bar: Download and Buy are both violet; "Not for sale" is
  // the tinted, disabled one.
  if (item.cta) item.cta = { ...item.cta, kind: cta.disabled ? "tinted" : "primary" };
  item.subtitle = barSubtitle(sound, { showCreator });
  // Keys print with the engraved signs: "F♯m · 120 BPM".
  const key = sound.key ? displayKey(sound.key) : null;
  item.meta = [key, sound.bpm != null ? `${Math.round(sound.bpm)} BPM` : null].filter(Boolean).join(" · ");
  item.keyBpm = keyBpmText({ ...sound, key });
  item[MARK] = true;
  return item;
}

// opts: { me, color, itemFor(sound) } (itemFor replaces barItem).
export function playIn(sound, list, opts = {}) {
  if (!sound) return;
  if (audition.isCurrent(sound.hash)) {
    audition.toggle();
    return;
  }
  const queue = Array.isArray(list) ? list : [sound];
  const item = opts.itemFor ? opts.itemFor(sound) : barItem(sound, opts);
  item[MARK] = true;
  audition.playSound(sound, { queue, index: queue.findIndex((s) => s.hash === sound.hash), item });
}

// ↑ ↓ from the keyboard: with nothing of this list playing, ↓ starts the
// first sound.
export function step(list, delta, opts = {}) {
  if (!Array.isArray(list) || !list.length) return;
  const i = list.findIndex((s) => audition.isCurrent(s.hash));
  const next = i < 0 ? list[delta > 0 ? 0 : list.length - 1] : list[Math.min(Math.max(i + delta, 0), list.length - 1)];
  if (next && !audition.isCurrent(next.hash)) playIn(next, list, opts);
}

// Space: pause or play what is up; with nothing of this list up, the first.
export function toggleIn(list, opts = {}) {
  if (!Array.isArray(list) || !list.length) return;
  const cur = list.find((s) => audition.isCurrent(s.hash));
  if (cur) audition.toggle();
  else playIn(list[0], list, opts);
}

// The bar's own next and prev step through audition's queue with a plain
// item (no call to action that acts, no Add to session). Until audition
// keeps an item builder for its queue (asked of W1), dress the current one
// again, in the engine's state and in the bar's.
export function redecorate(list, opts = {}) {
  const st = audition.state;
  const it = st && st.item;
  if (!it || it[MARK] || !Array.isArray(list)) return;
  const sound = list.find((s) => s.hash === it.id);
  if (!sound) return;
  const item = opts.itemFor ? opts.itemFor(sound) : barItem(sound, opts);
  item[MARK] = true;
  st.item = item;
  nowPlaying.auditionUpdated(item);
}

export default { barItem, barSubtitle, whereOf, playIn, step, toggleIn, redecorate, runCta };
